import React, { useCallback, useEffect, useRef } from 'react';
import WalkthroughRunner, { WALKTHROUGH_AUTO_START } from '../components/WalkthroughRunner';
import { CREATE_MODEL_WALKTHROUGH_ID } from '../registry';
import { applyDemoPageTransition, applyDemoStep } from './demoScript';
import { WIZARD_API, useWizardApiRegistry } from './wizardApi';

/** How long the page's Next button spins before the demo turns the page (Figma 9455:6826). */
export const DEMO_NEXT_LOADING_MS = 1500;

/**
 * The create-model walkthrough in demo mode: every mark auto-fills through
 * `demoScript.js` when the user activates the highlighted element or Next.
 *
 * The tour itself is started by `useDemoTour` (the runner never auto-starts
 * it). Once the last mark of a page is done the runner reports it is waiting
 * for the next page (`onAwaitingPage`), or that the tour is over
 * (`onFinished`, after Model Setup's last mark), and this component turns
 * the page the way the user's Next click would — after showing the Next
 * button busy for `DEMO_NEXT_LOADING_MS`. That last turn is Finish & Train,
 * which opens Review Training Plan. Start Training ends the demo.
 *
 * Back on a page's first mark lands on the previous page's last mark
 * (`onBackPage`): the page is turned back at once through the wizard's
 * `goToStep`, the way the page's own Back button would.
 *
 * Must render inside `DemoScope`, which provides the `WizardApiProvider` the
 * script reads the page APIs from.
 */
export default function DemoTourRunner({ currentPage, pageOrder }) {
    const registry = useWizardApiRegistry();
    const onFill = useCallback(
        (step) => {
            void applyDemoStep(step.id, registry);
        },
        [registry]
    );

    // Pending page turn; dropped (never run) if the demo unmounts first.
    const timerRef = useRef(null);
    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = null;
        },
        []
    );

    const turnPage = useCallback(
        (from) => {
            if (timerRef.current) return;
            const wizard = () => registry?.get?.(WIZARD_API.wizard);
            wizard()?.setNextLoading?.(true);
            timerRef.current = setTimeout(async () => {
                timerRef.current = null;
                try {
                    await applyDemoPageTransition(from, registry);
                } finally {
                    wizard()?.setNextLoading?.(false);
                }
            }, DEMO_NEXT_LOADING_MS);
        },
        [registry]
    );

    const onAwaitingPage = useCallback(({ from }) => turnPage(from), [turnPage]);
    const onBackPage = useCallback(({ to }) => registry?.get?.(WIZARD_API.wizard)?.goToStep?.(to), [registry]);
    const currentPageRef = useRef(currentPage);
    currentPageRef.current = currentPage;
    const onFinished = useCallback(() => turnPage(currentPageRef.current), [turnPage]);

    return (
        <WalkthroughRunner
            walkthroughId={CREATE_MODEL_WALKTHROUGH_ID}
            currentPage={currentPage}
            pageOrder={pageOrder}
            autoStart={WALKTHROUGH_AUTO_START.never}
            onFill={onFill}
            onAwaitingPage={onAwaitingPage}
            onBackPage={onBackPage}
            onFinished={onFinished}
        />
    );
}
